import { Column } from 'primereact/column';
import {
	DataTable,
	type DataTableExpandedRows,
	type DataTableValue,
} from 'primereact/datatable';
import { useState } from 'react';
import { MdArrowDropDown, MdArrowRight } from 'react-icons/md';
import {
	buildPaginatorTemplate,
	PAGINATOR_CURRENT_REPORT,
	paginatorPassThrough,
} from '@/components/table/pagination/Paginator';
import RowExpansionTemplate from './RowExpansionTemplate';
import styles from './request-table.module.css';

interface TableProps<T extends DataTableValue> {
	data: T[];
	columns: { field: keyof T; header: string; sortable?: boolean }[];
	emptyState?: 'noActiveRides' | 'noData' | 'error';
	loading?: boolean;
	onActionComplete?: () => void;
}

const getStatusStyle = (status: string) => {
	switch (status) {
		case 'Waiting for Approval':
			return styles.statusOnTrip;
		case 'Approved':
			return styles.statusCompleted;
		case 'Declined':
			return styles.statusCancelled;
		default:
			return styles.statusDefault;
	}
};

const RequestTable = <T extends DataTableValue>({
	data,
	columns,
	loading,
	onActionComplete,
}: TableProps<T>) => {
	const [expandedRows, setExpandedRows] = useState<
		DataTableExpandedRows | DataTableValue[] | undefined
	>(undefined);

	const isRowExpanded = (rowData: DataTableValue) =>
		Array.isArray(expandedRows)
			? expandedRows.some((row) => row === rowData)
			: false;

	const [options, setOptions] = useState({ skip: 0, take: 5 });

	return (
		<DataTable
			value={loading ? Array(options.take).fill({}) : data}
			totalRecords={data.length}
			paginator={!loading}
			rowsPerPageOptions={[5, 10, 25, 50]}
			first={options.skip}
			rows={options.take}
			onPage={(event) => {
				setOptions({
					...options,
					skip: event.first,
					take: event.rows,
				});
			}}
			className={styles.customDatatable}
			tableStyle={{ minWidth: '50rem', width: '100%' }}
			pt={{ paginator: paginatorPassThrough }}
			paginatorTemplate={buildPaginatorTemplate()}
			currentPageReportTemplate={PAGINATOR_CURRENT_REPORT}
			expandedRows={expandedRows}
			onRowToggle={(e) => setExpandedRows(e.data)}
			rowExpansionTemplate={(rowData) => <RowExpansionTemplate data={rowData} onActionComplete={onActionComplete} />}
		>
			<Column
				header=""
				body={(rowData) =>
					loading ? null : (
						<button
							type="button"
							onClick={() =>
								setExpandedRows((prev) => {
									const prevArray = Array.isArray(prev)
										? prev
										: [];
									return isRowExpanded(rowData)
										? prevArray.filter(
											(row) => row !== rowData,
										)
										: [...prevArray, rowData];
								})
							}
							className={styles.expandToggle}
						>
							{isRowExpanded(rowData) ? (
								<MdArrowDropDown
									size={24}
									className="text-tertiary"
								/>
							) : (
								<MdArrowRight
									size={24}
									className="text-black"
								/>
							)}
						</button>
					)
				}
				style={{ width: '3rem', textAlign: 'center' }}
			/>
			{columns.map((col) => (
				<Column
					key={String(col.field)}
					field={col.field as string}
					header={col.header}
					sortable={!loading && (col.sortable ?? false)}
					body={(rowData) => {
						if (loading) return '';

						const cellValue = rowData[col.field];

						if (col.header.toLowerCase() === 'status') {
							return (
								<span
									className={`${styles.statusBadge} ${getStatusStyle(cellValue)}`}
								>
									{cellValue}
								</span>
							);
						}

						return cellValue;
					}}
				/>
			))}
		</DataTable>
	);
};

export default RequestTable;
